import { toDate } from "@flaminem/utils";

import {
  ShortScenarioFragmentFragment,
  ScenarioVersionFragmentFragment,
  FullScenarioFragmentFragment,
  ScenarioVersionWithRulesFragmentFragment,
  ScenarioExecutionMode as ApiScenarioExecutionMode,
  ScenarioExecutionResultWithCaseFragmentFragment,
  ScenarioExecutionResultFragmentFragment,
} from "src/graphql/anti-fraud/__generated__/graphql";
import { mapToAdminStatus } from "src/mappers/adminStatus.mappers";
import { mapToCaseSummary } from "src/mappers/case.mappers";
import { mapToRule } from "src/mappers/rule.mappers";
import { mapToUser } from "src/mappers/user.mappers";
import {
  ScenarioExecutionResult,
  FullScenario,
  ScenarioVersion,
  ScenarioVersionWithRules,
  ShortScenario,
  ScenarioExecutionMode,
  ScenarioExecutionResultWithCase,
} from "src/types/scenario.ts";

import { mapToAlertValidationDecision } from "./alert.mappers";

export const mapToShortScenario = (fragment: ShortScenarioFragmentFragment): ShortScenario => ({
  id: fragment.id,
  name: fragment.name,
  code: fragment.code,
  caseType: { code: fragment.caseType.code, name: fragment.caseType.name },
  threshold: fragment.activeVersion.threshold,
  adminStatus: mapToAdminStatus(fragment.adminStatus),
  description: fragment.description,
  rules: fragment.activeVersion.rules.map((rule) => ({
    createdAt: toDate(rule.createdAt),
    createdBy: mapToUser(rule.createdBy),
    rule: rule.rule ? mapToRule(rule.rule) : null,
    score: rule.score,
  })),
  hasDraftVersion: fragment.hasDraftVersion,
  activeVersion: mapToScenarioVersionWithRules(fragment.activeVersion),
});

export const mapToFullScenario = (fragment: FullScenarioFragmentFragment): FullScenario => ({
  ...mapToShortScenario(fragment),
  draftVersion: fragment.draftVersion ? mapToScenarioVersionWithRules(fragment.draftVersion) : null,
  archivedVersions: fragment.archivedVersions.map(mapToScenarioVersionWithRules),
});

export const mapToScenarioVersion = (
  fragment: ScenarioVersionFragmentFragment
): ScenarioVersion => ({
  id: fragment.id,
  name: fragment.name,
  description: fragment.description,
  threshold: fragment.threshold,
  activatedAt: fragment.activatedAt ? toDate(fragment.activatedAt) : null,
  archivedAt: fragment.archivedAt ? toDate(fragment.archivedAt) : null,
  adminStatus: mapToAdminStatus(fragment.adminStatus),
  status: fragment.status,
  scenario: fragment.scenario
    ? {
        id: fragment.scenario.id,
        name: fragment.scenario.name,
        caseTypeCode: fragment.scenario.caseType.code,
      }
    : null,
});

export const mapToScenarioVersionWithRules = (
  fragment: ScenarioVersionWithRulesFragmentFragment
): ScenarioVersionWithRules => ({
  ...mapToScenarioVersion(fragment),
  rules: fragment.rules.map((rule) => ({
    createdAt: toDate(rule.createdAt),
    createdBy: mapToUser(rule.createdBy),
    rule: rule.rule ? mapToRule(rule.rule) : null,
    score: rule.score,
  })),
});

const EXECUTION_MODE_MAP: { [key in ApiScenarioExecutionMode]: ScenarioExecutionMode } = {
  BATCH: "batch",
  INDIVIDUAL: "individual",
};

const mapToScenarioExecutionMode = (mode: ApiScenarioExecutionMode): ScenarioExecutionMode =>
  EXECUTION_MODE_MAP[mode];

export const mapToScenarioExecutionResult = (
  fragment:
    | ScenarioExecutionResultFragmentFragment
    | ScenarioExecutionResultWithCaseFragmentFragment
): ScenarioExecutionResult => ({
  id: fragment.id,
  globalScore: fragment.globalScore,
  date: toDate(fragment.date),
  mode: mapToScenarioExecutionMode(fragment.mode),
  scenarioVersion: fragment.scenarioVersion ? mapToScenarioVersion(fragment.scenarioVersion) : null,
  alert: fragment.alert
    ? {
        id: fragment.alert.id,
        adminStatus: mapToAdminStatus(fragment.alert.adminStatus),
        decision: fragment.alert.decision
          ? mapToAlertValidationDecision(fragment.alert.decision)
          : null,
      }
    : null,
  triggeredBy: mapToUser(fragment.triggeredBy),
  ruleExecutionDetails: fragment.ruleExecutionDetails.map((executionDetails) => ({
    id: executionDetails.id,
    rule: executionDetails.rule ? mapToRule(executionDetails.rule) : null,
    ruleScore: executionDetails.ruleScore,
  })),
});

export const mapToScenarioExecutionResultWithCase = (
  fragment: ScenarioExecutionResultWithCaseFragmentFragment
): ScenarioExecutionResultWithCase => ({
  ...mapToScenarioExecutionResult(fragment),
  creationCase: fragment.caseVersions.creation
    ? mapToCaseSummary(fragment.caseVersions.creation)
    : null,
  currentCase: fragment.caseVersions.current
    ? mapToCaseSummary(fragment.caseVersions.current)
    : null,
});
